import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchAdminWorkspace } from '../../services/lmsDataCache';
import { useAuth } from '../../contexts/AuthContext';
import { Spinner } from '../ui/Spinner';
import {
  logEnquiry,
  markAttendanceFromReception,
  quickRegisterStudent,
  recordAdhocReceptionPayment,
  recordReceptionPayment,
} from '../../utils/lmsMutations';
import {
  getBranchFromCourses,
  getStudentDisplayName,
  getStudentRegistrationDate,
  resolveActiveCourseContexts,
  resolvePortfolioCourses,
} from '../../utils/studentIdentity';

import KioskShell from './kiosk/KioskShell';
import QrScannerModal from './kiosk/QrScannerModal';
import AttendanceSheet from './kiosk/AttendanceSheet';
import PaymentSheet from './kiosk/PaymentSheet';
import QuickRegisterSheet from './kiosk/QuickRegisterSheet';
import CredentialsCard from './kiosk/CredentialsCard';
import TodayRoster from './kiosk/TodayRoster';
import DuesQueue from './kiosk/DuesQueue';
import EnquiryForm from './kiosk/EnquiryForm';

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString() : 'Not set';

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const Pill = ({ tone = 'neutral', children }) => {
  const tones = {
    neutral: 'bg-stone-100 text-stone-700',
    success: 'bg-emerald-100 text-emerald-800',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800',
    info: 'bg-[#fce7f3] text-[#b91c66]',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
        tones[tone] || tones.neutral
      }`}
    >
      {children}
    </span>
  );
};

const ReceptionKioskPage = () => {
  const { currentUser } = useAuth();
  const { academyStudentId: academyStudentIdParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const onReceptionRoute = location.pathname.startsWith('/reception/kiosk');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [loggingEnquiry, setLoggingEnquiry] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [dataSet, setDataSet] = useState({
    users: {},
    students: {},
    courses: {},
    coursesList: [],
    enrollments: [],
    paymentRequests: [],
    payments: [],
    attendanceByStudent: {},
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [search, setSearch] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [welcomeToast, setWelcomeToast] = useState(null);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [attendanceError, setAttendanceError] = useState('');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [initialRequestId, setInitialRequestId] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [credentialsResult, setCredentialsResult] = useState(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryError, setEnquiryError] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const loadWorkspace = useCallback(async ({ force = false } = {}) => {
    setLoading(true);
    try {
      const workspace = await fetchAdminWorkspace({ force });
      const users = workspace.users || {};
      const students = workspace.students || {};
      const courses = workspace.courses || {};
      const enrollments = workspace.enrollments || [];
      const paymentRequests = workspace.paymentRequests || [];
      const payments = workspace.payments || [];
      const attendanceByStudent = workspace.attendanceByStudent || {};

      const coursesList = Object.entries(courses).map(([id, course]) => ({
        id,
        ...(course || {}),
      }));

      const studentDirectory = Object.keys(students).map((id) => {
        const student = students[id] || {};
        const user = users[id] || {};
        const studentEnrollments = enrollments.filter(
          (enrollment) => enrollment.studentId === id,
        );
        const activeCourses = resolveActiveCourseContexts({
          enrollments: studentEnrollments,
          courses,
        });

        return {
          id,
          user,
          student,
          academyStudentId:
            student.academyStudentId || user.academyStudentId || 'Not assigned',
          displayName: getStudentDisplayName(user, student),
          registrationDate: getStudentRegistrationDate(user, student),
          branch: getBranchFromCourses({
            student,
            enrollments: studentEnrollments,
            courses,
          }),
          activeCourseCount: activeCourses.length,
          photoURL:
            student.documents?.photoURL ||
            student.photoURL ||
            user.photoURL ||
            '',
          phone:
            student.personalDetails?.phone ||
            user.phone ||
            student.phone ||
            '',
          dueCount: paymentRequests.filter(
            (request) =>
              request.studentId === id &&
              ['pending', 'processing'].includes(request.status),
          ).length,
        };
      });

      const attendanceActivity = Object.entries(attendanceByStudent).flatMap(
        ([studentId, records]) =>
          records.map((record) => ({
            id: `att-${record.id || studentId}-${record.markedAt || record.date}`,
            type: 'attendance',
            studentId,
            studentName: record.studentName || '',
            courseName: record.courseName || 'Class',
            at: record.markedAt || record.date,
            status: record.status || 'present',
          })),
      );

      const paymentActivity = payments.map((payment) => ({
        id: `pay-${payment.id}`,
        type: 'payment',
        studentId: payment.studentId,
        studentName: payment.studentName || '',
        courseName: payment.courseName || 'Payment',
        amount: Number(payment.amount || 0),
        paymentMethod: payment.paymentMethod,
        at: payment.paymentDate || payment.createdAt,
        status: payment.status,
      }));

      const activity = [...attendanceActivity, ...paymentActivity]
        .filter((entry) => entry.at)
        .sort((a, b) => new Date(b.at) - new Date(a.at))
        .slice(0, 20);

      setDataSet({
        users,
        students,
        courses,
        coursesList,
        enrollments,
        paymentRequests,
        payments,
        attendanceByStudent,
      });
      setDirectory(
        studentDirectory.sort((a, b) =>
          a.displayName.localeCompare(b.displayName),
        ),
      );
      setRecentActivity(activity);
    } catch (error) {
      console.error('Failed to load kiosk workspace:', error);
      setFeedback({ type: 'error', message: 'Failed to load kiosk workspace.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    if (!academyStudentIdParam || !directory.length || selectedStudentId) return;
    const matched = directory.find(
      (item) => item.academyStudentId === academyStudentIdParam,
    );
    if (matched) {
      setSelectedStudentId(matched.id);
      setActiveTab('home');
      setWelcomeToast({
        name: matched.displayName,
        academyStudentId: matched.academyStudentId,
        photoURL: matched.photoURL,
        branch: matched.branch,
        at: new Date().toISOString(),
      });
    }
  }, [academyStudentIdParam, directory, selectedStudentId]);

  const selectedStudent = useMemo(
    () => directory.find((item) => item.id === selectedStudentId) || null,
    [directory, selectedStudentId],
  );

  const selectedStudentEnrollments = useMemo(
    () =>
      dataSet.enrollments.filter(
        (enrollment) => enrollment.studentId === selectedStudentId,
      ),
    [dataSet.enrollments, selectedStudentId],
  );

  const activeCourses = useMemo(
    () =>
      resolveActiveCourseContexts({
        enrollments: selectedStudentEnrollments,
        courses: dataSet.courses,
      }),
    [selectedStudentEnrollments, dataSet.courses],
  );

  const openRequestsForStudent = useMemo(
    () =>
      dataSet.paymentRequests.filter(
        (request) =>
          request.studentId === selectedStudentId &&
          ['pending', 'processing'].includes(request.status),
      ),
    [dataSet.paymentRequests, selectedStudentId],
  );

  const completedCourses = useMemo(
    () =>
      resolvePortfolioCourses({
        enrollments: selectedStudentEnrollments,
        courses: dataSet.courses,
      }),
    [selectedStudentEnrollments, dataSet.courses],
  );

  const studentAttendance = useMemo(
    () =>
      (dataSet.attendanceByStudent[selectedStudentId] || [])
        .slice()
        .sort(
          (a, b) =>
            new Date(b.markedAt || b.date || 0) -
            new Date(a.markedAt || a.date || 0),
        ),
    [dataSet.attendanceByStudent, selectedStudentId],
  );

  const recentStudentPayments = useMemo(
    () =>
      dataSet.payments
        .filter((payment) => payment.studentId === selectedStudentId)
        .sort((a, b) => new Date(b.paymentDate || 0) - new Date(a.paymentDate || 0))
        .slice(0, 5),
    [dataSet.payments, selectedStudentId],
  );

  const filteredDirectory = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return directory.slice(0, 12);
    return directory.filter(
      (item) =>
        item.displayName.toLowerCase().includes(needle) ||
        item.academyStudentId.toLowerCase().includes(needle) ||
        (item.phone || '').toLowerCase().includes(needle),
    );
  }, [directory, search]);

  const openRequestsCount = useMemo(
    () =>
      dataSet.paymentRequests.filter((request) =>
        ['pending', 'processing'].includes(request.status),
      ).length,
    [dataSet.paymentRequests],
  );

  const todayStats = useMemo(() => {
    const isToday = (iso) => {
      if (!iso) return false;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return false;
      const now = new Date();
      return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
      );
    };
    let attendanceToday = 0;
    Object.values(dataSet.attendanceByStudent || {}).forEach((records) => {
      records.forEach((record) => {
        if (isToday(record.markedAt || record.date)) attendanceToday += 1;
      });
    });
    const paymentsToday = dataSet.payments.filter(
      (payment) =>
        payment.status === 'completed' && isToday(payment.paymentDate),
    );
    const paymentsAmount = paymentsToday.reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0,
    );
    return {
      attendanceToday,
      paymentsCount: paymentsToday.length,
      paymentsAmount,
      openDues: openRequestsCount,
    };
  }, [dataSet.attendanceByStudent, dataSet.payments, openRequestsCount]);

  const selectStudentByAcademyId = useCallback(
    (academyId) => {
      const matched = directory.find(
        (item) => item.academyStudentId === academyId,
      );
      if (!matched) {
        setFeedback({
          type: 'error',
          message: `No student found for ID ${academyId}.`,
        });
        return null;
      }
      setSelectedStudentId(matched.id);
      setActiveTab('home');
      setWelcomeToast({
        name: matched.displayName,
        academyStudentId: matched.academyStudentId,
        photoURL: matched.photoURL,
        branch: matched.branch,
        at: new Date().toISOString(),
      });
      setFeedback({ type: '', message: '' });
      if (onReceptionRoute) {
        navigate(
          `/reception/kiosk/${encodeURIComponent(matched.academyStudentId)}`,
          { replace: true },
        );
      }
      return matched;
    },
    [directory, navigate, onReceptionRoute],
  );

  useEffect(() => {
    if (!welcomeToast) return undefined;
    const timer = setTimeout(() => setWelcomeToast(null), 4500);
    return () => clearTimeout(timer);
  }, [welcomeToast]);

  const handleScanResult = (academyId) => {
    setScannerOpen(false);
    selectStudentByAcademyId(academyId);
  };

  const handleAttendanceSubmit = async ({ course, notes }) => {
    if (!selectedStudent) return;
    setSaving(true);
    setAttendanceError('');
    try {
      await markAttendanceFromReception({
        studentId: selectedStudent.id,
        academyStudentId: selectedStudent.academyStudentId,
        studentName: selectedStudent.displayName,
        courseId: course.courseId,
        courseName: course.courseName,
        batchId: course.batchId,
        batchName: course.batchName,
        branch: course.branch,
        operator: currentUser,
        notes,
      });
      setAttendanceOpen(false);
      setFeedback({ type: 'success', message: 'Attendance marked.' });
      await loadWorkspace({ force: true });
    } catch (error) {
      console.error('Failed to mark attendance:', error);
      setAttendanceError(error.message || 'Could not mark attendance.');
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentSubmit = async (payload) => {
    setSaving(true);
    setPaymentError('');
    try {
      if (payload.mode === 'request') {
        await recordReceptionPayment({
          paymentRequest: payload.paymentRequest,
          paymentMethod: payload.paymentMethod,
          receiptNumber: payload.receiptNumber,
          notes: payload.notes,
          paymentType: payload.paymentType,
          paymentCycle: payload.paymentCycle,
          paymentCycleLabel: payload.paymentCycleLabel,
          operator: currentUser,
        });
      } else {
        await recordAdhocReceptionPayment({
          studentId: selectedStudent.id,
          studentName: selectedStudent.displayName,
          courseId: payload.course.courseId,
          courseName: payload.course.courseName,
          batchId: payload.course.batchId,
          batchName: payload.course.batchName,
          amount: payload.amount,
          paymentMethod: payload.paymentMethod,
          receiptNumber: payload.receiptNumber,
          notes: payload.notes,
          paymentType: payload.paymentType,
          paymentCycle: payload.paymentCycle,
          paymentCycleLabel: payload.paymentCycleLabel,
          operator: currentUser,
        });
      }
      setPaymentOpen(false);
      setFeedback({ type: 'success', message: 'Payment recorded.' });
      await loadWorkspace({ force: true });
    } catch (error) {
      console.error('Failed to record payment:', error);
      setPaymentError(error.message || 'Could not record payment.');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickRegister = async (payload) => {
    setRegistering(true);
    setRegisterError('');
    try {
      const result = await quickRegisterStudent({
        ...payload,
        operator: currentUser,
      });
      setRegisterOpen(false);
      setCredentialsResult(result);
      setFeedback({ type: 'success', message: `Account created for ${result.studentName}.` });
      await loadWorkspace({ force: true });
      setSelectedStudentId(result.uid);
      setActiveTab('home');
    } catch (error) {
      console.error('Quick register failed:', error);
      setRegisterError(
        error.message ||
          'Could not create the student account. Check the email and try again.',
      );
    } finally {
      setRegistering(false);
    }
  };

  const handleEnquirySubmit = async (payload) => {
    setLoggingEnquiry(true);
    setEnquiryError('');
    try {
      await logEnquiry({ ...payload, operator: currentUser });
      setEnquiryOpen(false);
      setFeedback({ type: 'success', message: 'Enquiry saved.' });
    } catch (error) {
      console.error('Failed to log enquiry:', error);
      setEnquiryError(error.message || 'Could not save enquiry.');
    } finally {
      setLoggingEnquiry(false);
    }
  };

  const handleTodayRosterMark = async (rosterItem) => {
    setSelectedStudentId(rosterItem.student.id);
    setWelcomeToast({
      name: rosterItem.student.displayName,
      academyStudentId: rosterItem.student.academyStudentId,
      photoURL: rosterItem.student.photoURL,
      branch: rosterItem.course.branch,
      at: new Date().toISOString(),
    });
    setTimeout(() => setAttendanceOpen(true), 60);
  };

  const handleDuesCollect = (request) => {
    setSelectedStudentId(request.studentId);
    setInitialRequestId(request.id);
    setPaymentOpen(true);
  };

  const tabs = [
    { id: 'home', label: 'Home', icon: '🏠' },
    {
      id: 'today',
      label: 'Today',
      icon: '📅',
    },
    {
      id: 'dues',
      label: 'Dues',
      icon: '💰',
      badge: openRequestsCount || undefined,
    },
    { id: 'register', label: 'Register', icon: '➕' },
    { id: 'recent', label: 'Recent', icon: '🕒' },
  ];

  const handleTabChange = (tabId) => {
    if (tabId === 'home' && activeTab === 'home') {
      setSelectedStudentId('');
      setWelcomeToast(null);
      setSearch('');
      setFeedback({ type: '', message: '' });
      return;
    }
    if (tabId === 'home') {
      setSelectedStudentId('');
      setWelcomeToast(null);
      setSearch('');
      setFeedback({ type: '', message: '' });
    }
    setActiveTab(tabId);
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <KioskShell
        operator={currentUser}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      >
        {feedback.message ? (
          <div
            className={`mb-3 rounded-2xl px-4 py-3 text-sm ${
              feedback.type === 'error'
                ? 'bg-red-50 text-red-700'
                : 'bg-emerald-50 text-emerald-700'
            }`}
          >
            {feedback.message}
          </div>
        ) : null}

        {welcomeToast ? (
          <div className="mb-3 flex items-center gap-3 rounded-2xl border border-[#f4b6d2] bg-[#fdf2f8] px-4 py-3">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white">
              {welcomeToast.photoURL ? (
                // eslint-disable-next-line jsx-a11y/alt-text
                <img
                  src={welcomeToast.photoURL}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-lg font-semibold text-[#b91c66]">
                  {welcomeToast.name?.[0] || '?'}
                </span>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#b91c66]">
                Welcome
              </p>
              <p className="truncate text-base font-semibold text-stone-950">
                {welcomeToast.name}
              </p>
              <p className="truncate text-xs text-stone-600">
                {welcomeToast.academyStudentId} · {welcomeToast.branch || 'Branch'}
              </p>
            </div>
          </div>
        ) : null}

        {activeTab === 'home' ? (
          <ScanTab
            directory={filteredDirectory}
            search={search}
            onSearch={setSearch}
            onOpenScanner={() => setScannerOpen(true)}
            onPickStudent={(student) => {
              setSelectedStudentId(student.id);
              setWelcomeToast({
                name: student.displayName,
                academyStudentId: student.academyStudentId,
                photoURL: student.photoURL,
                branch: student.branch,
                at: new Date().toISOString(),
              });
            }}
            selectedStudent={selectedStudent}
            activeCourses={activeCourses}
            openRequests={openRequestsForStudent}
            attendance={studentAttendance}
            recentPayments={recentStudentPayments}
            completedCourses={completedCourses}
            onOpenAttendance={() => setAttendanceOpen(true)}
            onOpenPayment={() => {
              setInitialRequestId('');
              setPaymentOpen(true);
            }}
            onQuickRegister={() => setRegisterOpen(true)}
            onClearSelection={() => setSelectedStudentId('')}
            operator={currentUser}
            todayStats={todayStats}
            recentActivity={recentActivity}
            onOpenDues={() => handleTabChange('dues')}
            onOpenToday={() => handleTabChange('today')}
          />
        ) : null}

        {activeTab === 'today' ? (
          <TodayRoster
            directory={directory}
            enrollments={dataSet.enrollments}
            courses={dataSet.courses}
            attendanceToday={dataSet.attendanceByStudent}
            onMark={handleTodayRosterMark}
          />
        ) : null}

        {activeTab === 'dues' ? (
          <DuesQueue
            paymentRequests={dataSet.paymentRequests}
            directory={directory}
            onCollect={handleDuesCollect}
          />
        ) : null}

        {activeTab === 'register' ? (
          <RegisterTab
            onQuickRegister={() => setRegisterOpen(true)}
            onEnquiry={() => setEnquiryOpen(true)}
          />
        ) : null}

        {activeTab === 'recent' ? (
          <RecentActivityTab activity={recentActivity} />
        ) : null}
      </KioskShell>

      <QrScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onResult={handleScanResult}
      />

      <AttendanceSheet
        open={attendanceOpen}
        onClose={() => setAttendanceOpen(false)}
        student={selectedStudent}
        activeCourses={activeCourses}
        onSubmit={handleAttendanceSubmit}
        saving={saving}
        error={attendanceError}
      />

      <PaymentSheet
        open={paymentOpen}
        onClose={() => {
          setPaymentOpen(false);
          setInitialRequestId('');
        }}
        student={selectedStudent}
        activeCourses={activeCourses}
        openRequests={openRequestsForStudent}
        onSubmit={handlePaymentSubmit}
        saving={saving}
        error={paymentError}
        initialRequestId={initialRequestId}
      />

      <QuickRegisterSheet
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        courses={dataSet.coursesList}
        onSubmit={handleQuickRegister}
        saving={registering}
        error={registerError}
      />

      <CredentialsCard
        open={Boolean(credentialsResult)}
        onClose={() => setCredentialsResult(null)}
        result={credentialsResult}
      />

      <EnquiryForm
        open={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        courses={dataSet.coursesList}
        onSubmit={handleEnquirySubmit}
        saving={loggingEnquiry}
        error={enquiryError}
      />
    </>
  );
};

const ScanTab = ({
  directory,
  search,
  onSearch,
  onOpenScanner,
  onPickStudent,
  selectedStudent,
  activeCourses,
  openRequests,
  attendance,
  recentPayments,
  completedCourses,
  onOpenAttendance,
  onOpenPayment,
  onQuickRegister,
  onClearSelection,
  operator,
  todayStats,
  recentActivity,
  onOpenDues,
  onOpenToday,
}) => {
  return (
    <div className="mx-auto grid w-full max-w-[1600px] gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="space-y-4">
        <div className="rounded-2xl border border-stone-200 bg-white p-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onOpenScanner}
              className="flex min-h-[64px] flex-1 items-center justify-center gap-2 rounded-2xl bg-[#d61f74] px-5 text-sm font-semibold text-white shadow-[0_14px_32px_-18px_rgba(214,31,116,0.8)]"
            >
              <span className="text-xl">📷</span> Scan student QR
            </button>
            <button
              type="button"
              onClick={onQuickRegister}
              className="min-h-[64px] flex-1 rounded-2xl border border-stone-300 bg-white px-5 text-sm font-semibold text-stone-800"
            >
              ➕ Quick register
            </button>
          </div>
          <label className="mt-3 block">
            <span className="sr-only">Search students</span>
            <input
              type="text"
              value={search}
              onChange={(event) => onSearch(event.target.value)}
              placeholder="Search name, ID, or phone"
              className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </label>
        </div>

        {selectedStudent ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-stone-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-stone-100">
                  {selectedStudent.photoURL ? (
                    // eslint-disable-next-line jsx-a11y/alt-text
                    <img
                      src={selectedStudent.photoURL}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl font-semibold text-stone-500">
                      {selectedStudent.displayName?.[0] || '?'}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-lg font-semibold text-stone-950">
                    {selectedStudent.displayName}
                  </p>
                  <p className="truncate text-sm text-stone-600">
                    {selectedStudent.academyStudentId}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Pill tone="info">{selectedStudent.branch}</Pill>
                    <Pill tone={activeCourses.length ? 'success' : 'neutral'}>
                      {activeCourses.length} active
                    </Pill>
                    <Pill tone={openRequests.length ? 'warning' : 'neutral'}>
                      {openRequests.length} due
                    </Pill>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClearSelection}
                  className="min-h-[40px] rounded-full border border-stone-200 px-3 text-xs font-semibold text-stone-600"
                >
                  Clear
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={onOpenAttendance}
                  disabled={!activeCourses.length}
                  className="min-h-[56px] rounded-2xl bg-stone-900 px-4 text-sm font-semibold text-white disabled:opacity-40"
                >
                  ✅ Mark present
                </button>
                <button
                  type="button"
                  onClick={onOpenPayment}
                  disabled={!activeCourses.length && !openRequests.length}
                  className="min-h-[56px] rounded-2xl bg-[#d61f74] px-4 text-sm font-semibold text-white disabled:opacity-40"
                >
                  💰 Take payment
                </button>
              </div>
            </div>

            {openRequests.length ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                  Open dues
                </p>
                <div className="mt-2 space-y-1">
                  {openRequests.map((request) => (
                    <div
                      key={request.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="truncate pr-3 text-stone-800">
                        {request.courseName || 'Course'} ·{' '}
                        {request.paymentType || 'fee'}
                      </span>
                      <span className="font-semibold text-stone-900">
                        LKR {Number(request.amount || 0).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-4 py-8 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
              No student selected
            </p>
            <p className="mt-1 text-base font-semibold text-stone-900">
              Scan a QR or pick from the list below
            </p>
          </div>
        )}

        <div className="rounded-2xl border border-stone-200 bg-white">
          <p className="border-b border-stone-100 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-stone-500">
            {search ? `Results (${directory.length})` : 'Recent students'}
          </p>
          {directory.length ? (
            <div className="divide-y divide-stone-100">
              {directory.map((item) => {
                const active = selectedStudent?.id === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onPickStudent(item)}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left transition ${
                      active ? 'bg-[#fdf2f8]' : 'hover:bg-stone-50'
                    }`}
                  >
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-stone-100">
                      {item.photoURL ? (
                        // eslint-disable-next-line jsx-a11y/alt-text
                        <img
                          src={item.photoURL}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-sm font-semibold text-stone-500">
                          {item.displayName?.[0] || '?'}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-stone-950">
                        {item.displayName}
                      </p>
                      <p className="truncate text-xs text-stone-600">
                        {item.academyStudentId}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Pill tone={item.activeCourseCount ? 'success' : 'neutral'}>
                        {item.activeCourseCount} active
                      </Pill>
                      {item.dueCount ? (
                        <Pill tone="warning">{item.dueCount} due</Pill>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="px-4 py-6 text-center text-sm text-stone-500">
              No students match this search.
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {selectedStudent ? (
          <>
            <div className="rounded-2xl border border-stone-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                Summary
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <SummaryTile
                  label="Registered"
                  value={formatDate(selectedStudent.registrationDate)}
                />
                <SummaryTile
                  label="Completed"
                  value={`${completedCourses.length} courses`}
                />
                <SummaryTile
                  label="Attendance"
                  value={`${attendance.length} marks`}
                />
                <SummaryTile
                  label="Phone"
                  value={selectedStudent.phone || '—'}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-stone-200 bg-white">
              <p className="border-b border-stone-100 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-stone-500">
                Recent attendance
              </p>
              {attendance.length ? (
                <ul className="divide-y divide-stone-100">
                  {attendance.slice(0, 5).map((record) => (
                    <li
                      key={record.id || `${record.markedAt}-${record.courseId}`}
                      className="flex items-center justify-between px-4 py-2 text-sm"
                    >
                      <span className="truncate pr-3 text-stone-800">
                        {record.courseName || 'Class'}
                      </span>
                      <span className="flex-shrink-0 text-xs text-stone-500">
                        {formatDateTime(record.markedAt || record.date)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-4 py-4 text-sm text-stone-500">
                  No attendance marks yet.
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-stone-200 bg-white">
              <p className="border-b border-stone-100 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-stone-500">
                Recent payments
              </p>
              {recentPayments.length ? (
                <ul className="divide-y divide-stone-100">
                  {recentPayments.map((payment) => (
                    <li
                      key={payment.id}
                      className="flex items-center justify-between px-4 py-2 text-sm"
                    >
                      <div className="min-w-0 pr-3">
                        <p className="truncate font-medium text-stone-900">
                          {payment.courseName || 'Payment'}
                        </p>
                        <p className="truncate text-xs text-stone-500">
                          {formatDateTime(payment.paymentDate)} ·{' '}
                          {payment.paymentMethod}
                        </p>
                      </div>
                      <span className="flex-shrink-0 text-sm font-semibold text-stone-900">
                        LKR {Number(payment.amount || 0).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-4 py-4 text-sm text-stone-500">
                  No payment activity yet.
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="hidden space-y-4 lg:block">
            <IdleSidePanel
              operator={operator}
              todayStats={todayStats}
              recentActivity={recentActivity}
              onOpenDues={onOpenDues}
              onOpenToday={onOpenToday}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const IdleSidePanel = ({
  operator,
  todayStats,
  recentActivity = [],
  onOpenDues,
  onOpenToday,
}) => {
  const operatorName =
    [operator?.firstName, operator?.lastName].filter(Boolean).join(' ') ||
    operator?.email ||
    'Reception';
  const initial = operatorName.charAt(0).toUpperCase();
  const latest = recentActivity.slice(0, 4);

  return (
    <>
      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#fce7f3] text-base font-semibold text-[#b91c66]">
            {operator?.photoURL ? (
              // eslint-disable-next-line jsx-a11y/alt-text
              <img
                src={operator.photoURL}
                className="h-full w-full object-cover"
              />
            ) : (
              <span>{initial}</span>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
              Signed in
            </p>
            <p className="truncate text-sm font-semibold text-stone-900">
              {operatorName}
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
          Today at a glance
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <button
            type="button"
            onClick={onOpenToday}
            className="rounded-xl border border-stone-100 bg-stone-50 px-3 py-2 text-left transition hover:border-stone-200 hover:bg-white"
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              Attendance
            </p>
            <p className="mt-0.5 text-lg font-semibold text-stone-900">
              {todayStats?.attendanceToday || 0}
            </p>
          </button>
          <div className="rounded-xl border border-stone-100 bg-stone-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              Payments
            </p>
            <p className="mt-0.5 truncate text-lg font-semibold text-stone-900">
              LKR {Number(todayStats?.paymentsAmount || 0).toLocaleString()}
            </p>
          </div>
          <button
            type="button"
            onClick={onOpenDues}
            className="col-span-2 flex items-center justify-between rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-left transition hover:border-amber-200"
          >
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                Open dues
              </p>
              <p className="mt-0.5 text-lg font-semibold text-amber-900">
                {todayStats?.openDues || 0}
              </p>
            </div>
            <span className="text-xs font-semibold text-amber-800">View →</span>
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white">
        <p className="border-b border-stone-100 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-stone-500">
          Latest activity
        </p>
        {latest.length ? (
          <ul className="divide-y divide-stone-100">
            {latest.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start gap-2 px-4 py-2.5 text-sm"
              >
                <span className="mt-0.5 text-base">
                  {entry.type === 'attendance' ? '✅' : '💰'}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-stone-900">
                    {entry.studentName || 'Student'}
                  </p>
                  <p className="truncate text-xs text-stone-500">
                    {entry.type === 'attendance'
                      ? entry.courseName || 'Attendance'
                      : `LKR ${Number(entry.amount || 0).toLocaleString()}`}
                  </p>
                </div>
                <span className="flex-shrink-0 text-[11px] text-stone-400">
                  {formatDateTime(entry.at)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-6 text-center text-sm text-stone-500">
            Scan a student or record a payment to see activity here.
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-4 py-4 text-sm text-stone-600">
        <p className="font-semibold text-stone-800">Tip</p>
        <p className="mt-1">
          Tap <span className="font-semibold">Scan student QR</span> to check in
          the next student, or use the tabs below to open Today, Dues, or
          Register.
        </p>
      </div>
    </>
  );
};

const SummaryTile = ({ label, value }) => (
  <div className="rounded-xl border border-stone-100 bg-stone-50 px-3 py-2">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </p>
    <p className="mt-0.5 truncate text-sm font-semibold text-stone-900">{value}</p>
  </div>
);

const RegisterTab = ({ onQuickRegister, onEnquiry }) => (
  <div className="space-y-3">
    <button
      type="button"
      onClick={onQuickRegister}
      className="w-full rounded-2xl border border-stone-200 bg-white p-5 text-left transition hover:border-stone-300"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-stone-950">Quick register student</p>
          <p className="mt-1 text-sm text-stone-600">
            Name, phone, email → create login. Show credentials on-screen with
            Copy/Share. Student completes profile after first login.
          </p>
        </div>
        <span className="text-3xl">➕</span>
      </div>
    </button>
    <button
      type="button"
      onClick={onEnquiry}
      className="w-full rounded-2xl border border-stone-200 bg-white p-5 text-left transition hover:border-stone-300"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-stone-950">Walk-in enquiry</p>
          <p className="mt-1 text-sm text-stone-600">
            Capture prospective students (name, phone, interest) for later
            follow-up by admin.
          </p>
        </div>
        <span className="text-3xl">📋</span>
      </div>
    </button>
  </div>
);

const RecentActivityTab = ({ activity }) => {
  if (!activity.length) {
    return (
      <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-4 py-10 text-center text-sm text-stone-600">
        No recent activity yet. Scan-ins and payments show here.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {activity.map((entry) => (
        <div
          key={entry.id}
          className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-white px-4 py-3"
        >
          <span className="text-xl">
            {entry.type === 'attendance' ? '✅' : '💰'}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-stone-950">
              {entry.studentName || 'Student'}
            </p>
            <p className="truncate text-xs text-stone-600">
              {entry.type === 'attendance'
                ? `Attendance · ${entry.courseName}`
                : `Payment · ${entry.courseName}${
                    entry.amount
                      ? ` · LKR ${entry.amount.toLocaleString()}`
                      : ''
                  }`}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="text-[11px] text-stone-500">
              {formatDateTime(entry.at)}
            </span>
            {entry.type === 'payment' ? (
              <Pill
                tone={
                  entry.status === 'completed'
                    ? 'success'
                    : entry.status === 'failed'
                      ? 'danger'
                      : 'warning'
                }
              >
                {entry.status}
              </Pill>
            ) : (
              <Pill tone="success">Present</Pill>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReceptionKioskPage;
